import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { suppliersAPI, SupplierCustomer, SupplierSiteRef } from '../lib/api';
import { isCompanyAdminUser, isPlatformUser } from '../lib/companyEditAccess';
import { getApiErrorMessage } from '../utils/errorUtils';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import { useView } from '../contexts/ViewContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CustomerCard from '../components/suppliers/CustomerCard';
import CustomerChatDialog from '../components/suppliers/CustomerChatDialog';
import {
  Search,
  Loader2,
  Building2,
  ShoppingCart,
  Coins,
  TrendingUp,
} from 'lucide-react';

interface CustomerStats {
  total_customers: number;
  total_po_value: number;
  /** CR-7714 BL-14: customers left out of total_po_value for want of a rate. */
  total_po_value_missing: number;
  open_po_lines: number;
  total_pos: number;
}

export default function Customers() {
  const [customers, setCustomers] = useState<SupplierCustomer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatCustomerId, setChatCustomerId] = useState<number | null>(null);
  // CR-8599: the supplier sites in view, and the one Messages / the NDA act as.
  const [sitesInView, setSitesInView] = useState<SupplierSiteRef[]>([]);
  const [actingSupplier, setActingSupplier] = useState<SupplierSiteRef | null>(null);
  const [loadedViewKey, setLoadedViewKey] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { formatCurrency: _formatCurrency } = useFormatters();
  const { user } = useAuth();
  // CR-8599: viewKey changes whenever the top-bar picker moves site / group.
  const { viewKey, isLoading: viewLoading } = useView();
  // CR-8681 (user decision): the API serves a customer's profile to a supplier
  // ADMIN only (CompanyViewSet._is_supplier_admin_of_customer — admin role or
  // staff), so only they get the name as a link. Helix can open any profile.
  const canOpenProfile = isCompanyAdminUser(user) || isPlatformUser(user);
  const [supplierCurrency, setSupplierCurrency] = useState('GBP');

  useEffect(() => {
    // CR-8599: the list follows the picker — one site, or every site the user
    // holds in the picked group — so it re-reads whenever the scope moves. It
    // used to load once, for the home site only, whatever the picker said.
    if (viewLoading) return;
    let current = true;
    setLoading(true);
    setError(null);
    suppliersAPI.getMyCustomers()
      .then((response) => {
        if (!current) return;
        const customersData = response.data.results || [];
        // CR-5867: the response includes a top-level `currency` field driven
        // by the supplier company's base_currency, so the symbol and the
        // converted totals stay coherent across multi-country customers.
        if (response.data.currency) {
          setSupplierCurrency(response.data.currency);
        }
        setCustomers(customersData);
        setSitesInView(response.data.supplier_sites || []);
        setActingSupplier(response.data.acting_supplier || null);
        setLoadedViewKey(viewKey);

        setStats({
          total_customers: customersData.length,
          total_po_value: customersData.reduce((sum: number, c: SupplierCustomer) => sum + (c.total_value || 0), 0),
          total_po_value_missing: customersData.filter((c: SupplierCustomer) => c.total_value === null).length,
          open_po_lines: customersData.reduce((sum: number, c: SupplierCustomer) => sum + (c.open_lines || 0), 0),
          total_pos: customersData.reduce((sum: number, c: SupplierCustomer) => sum + (c.po_count || 0), 0),
        });
      })
      .catch((err: unknown) => {
        if (!current) return;
        console.error('Failed to load customers:', err);
        // The API says why (e.g. "Only suppliers can access customer list").
        setError(getApiErrorMessage(err, 'Customers could not be loaded.'));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, [viewKey, viewLoading, reloadKey]);

  // CR-8605: a customer's message bell links to /customers?messages=<customer id>.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCustomerId = Number(searchParams.get('messages')) || null;
  useEffect(() => {
    if (!linkedCustomerId) return;
    if (customers.some((c) => c.id === linkedCustomerId)) setChatCustomerId(linkedCustomerId);
  }, [linkedCustomerId, customers]);

  const closeChat = () => {
    setChatCustomerId(null);
    if (searchParams.has('messages')) {
      const next = new URLSearchParams(searchParams);
      next.delete('messages');
      setSearchParams(next, { replace: true });
    }
  };

  // Looked up by id so a scope switch from the Messages window re-reads the
  // customer (and the acting site) from the refreshed list.
  const chatCustomer = customers.find((c) => c.id === chatCustomerId) || null;
  const actingSupplierId = actingSupplier?.id ?? user?.company ?? null;
  // From the moment the picker moves until that scope's list is in, the list
  // and the acting site on screen belong to the previous scope.
  const refreshing = loading || loadedViewKey !== viewKey;
  // Name the supplying sites when there is more than one, or when the site
  // the user acts as is not among them (a group the home site is outside).
  const showSuppliedBy = sitesInView.length > 1
    || !sitesInView.some((site) => site.id === actingSupplierId);

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.country?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CURRENCY_SYMBOLS: Record<string, string> = {
    GBP: '£', USD: '$', EUR: '€', PLN: 'PLN ', CAD: 'CA$', AUD: 'A$',
  };
  const formatCurrency = (value: number) => {
    const symbol = CURRENCY_SYMBOLS[supplierCurrency] || supplierCurrency + ' ';
    return `${symbol}${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  };

  // The full-page spinner is for the first load only; a scope switch keeps the
  // page (and an open Messages window) in place while the new scope loads.
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading customers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => setReloadKey((key) => key + 1)} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Customers
            {refreshing && (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading customers" />
            )}
          </h1>
          <p className="text-muted-foreground">
            Companies that purchase from your company
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.total_pos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Coins className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Order Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_po_value)}</p>
                  {stats.total_po_value_missing > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="total-value-missing">
                      Excludes {stats.total_po_value_missing} customer{stats.total_po_value_missing === 1 ? '' : 's'} with no exchange rate on file
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open Lines</p>
                  <p className="text-2xl font-bold">{stats.open_po_lines}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customers by name, city, or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCustomers.map((customer) => (
          <CustomerCard
            key={customer.id}
            customer={customer}
            canOpenProfile={canOpenProfile}
            showSuppliedBy={showSuppliedBy}
            actingSupplierId={actingSupplierId}
            onMessages={() => setChatCustomerId(customer.id)}
          />
        ))}
      </div>

      {filteredCustomers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No customers found matching your search.'
                : 'No customers found.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Messages Dialog */}
      {chatCustomer && actingSupplierId && (
        <CustomerChatDialog
          supplierId={actingSupplierId}
          customerId={chatCustomer.id}
          customerName={chatCustomer.name}
          supplierSites={chatCustomer.supplier_sites}
          refreshing={refreshing}
          onClose={closeChat}
        />
      )}
    </div>
  );
}
